import React, { useEffect, useRef, useState } from 'react';
import OperationStatus from '@/components/ui/OperationStatus';
import { Capability, hasCapability } from '@/services/api/v2';
import { SeasonExportDataset, SeasonQuery } from '@/types';

const datasets: Array<{ value: SeasonExportDataset; label: string }> = [
  { value: 'results', label: 'Export results CSV' },
  { value: 'monthly', label: 'Export monthly CSV' },
  { value: 'standings', label: 'Export standings CSV' },
  { value: 'players', label: 'Export players CSV' },
];

export interface SeasonExportMenuProps {
  capabilities: readonly Capability[];
  query: SeasonQuery;
  onExport(dataset: SeasonExportDataset): Promise<void>;
  onCancel?(): void;
}

const SeasonExportMenu: React.FC<SeasonExportMenuProps> = ({ capabilities, onExport, onCancel }) => {
  const [pending, setPending] = useState<SeasonExportDataset | null>(null);
  const [message, setMessage] = useState('');
  const attempt = useRef(0);
  const running = useRef(false);
  useEffect(() => () => { attempt.current += 1; running.current = false; onCancel?.(); }, [onCancel]);
  if (!hasCapability(capabilities, 'season.export')) return null;
  const start = (dataset: SeasonExportDataset) => {
    if (running.current || pending !== null) return;
    running.current = true;
    const token = ++attempt.current;
    setPending(dataset);
    setMessage(`Preparing ${dataset} CSV.`);
    void onExport(dataset).then(() => {
      if (attempt.current === token) setMessage(`${dataset} CSV downloaded.`);
    }).catch(error => {
      if (attempt.current === token && (error as { code?: string })?.code !== 'canceled') setMessage((error as { message?: string })?.message ?? `Unable to export ${dataset} CSV.`);
    }).finally(() => {
      if (attempt.current === token) { running.current = false; setPending(null); }
    });
  };
  const cancel = () => {
    attempt.current += 1;
    running.current = false;
    onCancel?.();
    setPending(null);
    setMessage('Export canceled.');
  };
  return <div className="flex flex-wrap items-center gap-2">
    <details className="relative">
      <summary className="inline-flex min-h-11 cursor-pointer list-none items-center rounded-md border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800">Export CSV</summary>
      <div className="absolute right-0 z-20 mt-2 w-60 rounded-lg bg-white p-2 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-700">
        {datasets.map(dataset => <button key={dataset.value} type="button" disabled={pending !== null} onClick={() => start(dataset.value)} className="flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm font-semibold text-gray-800 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:opacity-50 dark:text-gray-100 dark:hover:bg-gray-800">{dataset.label}</button>)}
      </div>
    </details>
    {pending
      ? <OperationStatus state="pending" label="Exporting season" description={`Preparing ${pending} CSV.`} onCancel={cancel} />
      : message && <span className="text-sm text-gray-700 dark:text-gray-200" role="status" aria-live="polite">{message}</span>}
  </div>;
};

export default SeasonExportMenu;
